import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, ShoppingCart, Plus, Trash2, Sparkles, CreditCard, Gift, CheckCircle, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PLAN_PRICING } from '@/components/membership/types';
import { monthlyBenefits } from '@/components/membership/constants';

// Logos de membresías
import logoBronce from '@/assets/membresia-logos/logo-bronce-new.png';
import logoPlata from '@/assets/membresia-logos/logo-plata-new.png';
import logoOro from '@/assets/membresia-logos/logo-oro-new.png';

interface CartItem {
  id: string;
  name: string;
  category: string;
  originalPrice: number;
  discountedPrice: number;
  discountPercent: number;
}

interface BenefitItem {
  id: string;
  name: string;
  quantity: number;
  estimatedValue: number;
  icon: string;
}

interface CustomerSavingsCalculatorProps {
  onSelectPlan: (plan: any) => void;
  plans: any[];
}

const discountsByMembership: Record<string, Record<string, number>> = {
  bronce: {
    'Tecnología': 5,
    'Audio': 5,
    'Gaming': 5,
    'Estuches': 5,
    'Relojes': 5,
    'Cargadores': 5,
    'Reparaciones': 10,
    'Vidrios': 5,
  },
  plata: {
    'Tecnología': 10,
    'Audio': 10,
    'Gaming': 10,
    'Estuches': 10,
    'Relojes': 10,
    'Cargadores': 10,
    'Reparaciones': 15,
    'Vidrios': 10,
  },
  oro: {
    'Tecnología': 20,
    'Audio': 15,
    'Gaming': 20,
    'Estuches': 20,
    'Relojes': 10,
    'Cargadores': 20,
    'Reparaciones': 20,
    'Vidrios': 15,
  },
};

const categories = [
  'Tecnología',
  'Audio',
  'Gaming',
  'Estuches',
  'Relojes',
  'Cargadores',
  'Reparaciones',
  'Vidrios',
];

// Valores estimados por tipo de beneficio (en colones)
const benefitEstimatedValues: Record<string, number> = {
  vidrios: 2500,
  estuches: 6000,
  limpieza: 1500,
  hidrogel: 4000,
};

const membershipConfig = {
  bronce: {
    logo: logoBronce,
    name: 'Bronce',
    gradient: 'from-amber-700 to-amber-900',
    border: 'border-amber-500/50',
    bg: 'bg-amber-500/10',
    text: 'text-amber-400',
    accent: 'amber',
  },
  plata: {
    logo: logoPlata,
    name: 'Plata',
    gradient: 'from-slate-400 to-slate-600',
    border: 'border-slate-400/50',
    bg: 'bg-slate-400/10',
    text: 'text-slate-300',
    accent: 'slate',
  },
  oro: {
    logo: logoOro,
    name: 'Oro',
    gradient: 'from-yellow-500 to-amber-600',
    border: 'border-yellow-500/50',
    bg: 'bg-yellow-500/10',
    text: 'text-yellow-400',
    accent: 'yellow',
  },
};

const formatPrice = (price: number) => {
  return `₡${price.toLocaleString('es-CR')}`;
};

export const CustomerSavingsCalculator: React.FC<CustomerSavingsCalculatorProps> = ({
  onSelectPlan,
  plans,
}) => {
  const [selectedMembership, setSelectedMembership] = useState<'bronce' | 'plata' | 'oro'>('oro');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [productName, setProductName] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productPrice, setProductPrice] = useState('');

  const config = membershipConfig[selectedMembership];
  const membershipPrice = PLAN_PRICING[selectedMembership].mensual;
  const planBenefits = monthlyBenefits[selectedMembership];

  // Generar items de beneficios como items del carrito
  const benefitItems: BenefitItem[] = useMemo(() => {
    return Object.entries(planBenefits).map(([key, benefit]) => {
      // Determinar valor estimado basado en el tipo de beneficio
      let estimatedValue = 2000; // default
      if (key.includes('vidrio') || key.includes('temperado')) {
        estimatedValue = benefitEstimatedValues.vidrios;
      } else if (key.includes('estuche')) {
        estimatedValue = benefitEstimatedValues.estuches;
      } else if (key.includes('limpieza')) {
        estimatedValue = benefitEstimatedValues.limpieza;
      } else if (key.includes('hidrogel')) {
        estimatedValue = benefitEstimatedValues.hidrogel;
      }
      
      return {
        id: `benefit-${key}`,
        name: benefit.name,
        quantity: benefit.max,
        estimatedValue: estimatedValue * benefit.max,
        icon: benefit.icon || '🎁',
      };
    });
  }, [planBenefits]);

  // Total valor estimado de beneficios
  const totalBenefitsValue = useMemo(() => {
    return benefitItems.reduce((sum, item) => sum + item.estimatedValue, 0);
  }, [benefitItems]);

  // Recalcular carrito cuando cambia la membresía
  useEffect(() => {
    if (cart.length > 0) {
      const updatedCart = cart.map(item => {
        const newDiscountPercent = discountsByMembership[selectedMembership][item.category] || 0;
        const newDiscountedPrice = item.originalPrice * (1 - newDiscountPercent / 100);
        return {
          ...item,
          discountPercent: newDiscountPercent,
          discountedPrice: newDiscountedPrice,
        };
      });
      setCart(updatedCart);
    }
  }, [selectedMembership]);

  const addToCart = () => {
    if (!productName.trim() || !productCategory || !productPrice) return;

    const price = parseFloat(productPrice.replace(/[^0-9]/g, ''));
    if (isNaN(price) || price <= 0) return;

    const discountPercent = discountsByMembership[selectedMembership][productCategory] || 0;
    const discountedPrice = price * (1 - discountPercent / 100);

    const newItem: CartItem = {
      id: Date.now().toString(),
      name: productName.trim(),
      category: productCategory,
      originalPrice: price,
      discountedPrice,
      discountPercent,
    };

    setCart([...cart, newItem]);
    setProductName('');
    setProductPrice('');
  };

  const removeFromCart = (id: string) => {
    setCart(cart.filter((item) => item.id !== id));
  };

  const totalOriginal = cart.reduce((sum, item) => sum + item.originalPrice, 0);
  const totalDiscounted = cart.reduce((sum, item) => sum + item.discountedPrice, 0);
  const totalSavings = totalOriginal - totalDiscounted;
  const grandTotal = totalDiscounted + membershipPrice;

  const handleActivateMembership = () => {
    const plan = plans.find(
      (p) => p.name.toLowerCase().includes(selectedMembership)
    );
    if (plan) {
      onSelectPlan(plan);
    }
  };

  return (
    <section className="py-20 relative z-10">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-4">
            <Calculator className="w-5 h-5 text-blue-400" />
            <span className="text-blue-400 text-sm font-medium">Simulador de Ahorro</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            <span className="text-white">Calculá </span>
            <span className="text-blue-400">cuánto ahorrás</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Agregá tus productos al carrito y mirá el total con tu descuento de membresía
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`max-w-4xl mx-auto bg-zinc-900/80 backdrop-blur-sm border ${config.border} rounded-3xl p-6 md:p-8 shadow-lg`}
        >
          {/* Membership Selector - Sin precios */}
          <div className="mb-8">
            <p className="text-zinc-300 font-medium mb-4 text-center">
              🏆 Seleccioná tu membresía:
            </p>
            <div className="flex justify-center gap-3 md:gap-4">
              {(['bronce', 'plata', 'oro'] as const).map((tier) => {
                const tierConfig = membershipConfig[tier];
                const isSelected = selectedMembership === tier;
                return (
                  <button
                    key={tier}
                    onClick={() => setSelectedMembership(tier)}
                    className={`relative p-3 md:p-4 rounded-2xl border-2 transition-all duration-300 flex flex-col items-center ${
                      isSelected
                        ? `${tierConfig.border} ${tierConfig.bg} scale-105 shadow-lg`
                        : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
                    }`}
                  >
                    <img
                      src={tierConfig.logo}
                      alt={tierConfig.name}
                      className="h-12 md:h-16 w-auto"
                    />
                    {isSelected && (
                      <motion.div
                        layoutId="selected-tier"
                        className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gradient-to-r ${tierConfig.gradient}`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Product Form */}
          <div className="mb-8 p-4 bg-zinc-800/50 rounded-2xl border border-zinc-700">
            <p className="text-zinc-300 font-medium mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4 text-blue-400" />
              Agregá un producto:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Select value={productCategory} onValueChange={setProductCategory}>
                <SelectTrigger className="bg-zinc-900 border-zinc-600 text-white">
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-600">
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat} className="text-white hover:bg-zinc-700">
                      {cat} ({discountsByMembership[selectedMembership][cat]}% off)
                    </SelectItem>
                  ))
                  }
                </SelectContent>
              </Select>

              <Input
                placeholder="Nombre del producto"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500"
              />

              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400">₡</span>
                <Input
                  type="text"
                  placeholder="Precio"
                  value={productPrice}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setProductPrice(value);
                  }}
                  className="bg-zinc-900 border-zinc-600 text-white placeholder:text-zinc-500 pl-8"
                />
              </div>

              <Button
                onClick={addToCart}
                disabled={!productName.trim() || !productCategory || !productPrice}
                className="bg-gradient-to-r from-[#113B8A] to-[#1E5BB7] hover:opacity-90 text-white font-medium"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </div>
          </div>

          {/* Cart - Productos + Beneficios */}
          <div className="mb-8">
            <p className={`${config.text} font-medium mb-4 flex items-center gap-2`}>
              <ShoppingCart className="w-4 h-4" />
              Tu carrito:
            </p>

            <div className="space-y-4">
              {/* Productos Agregados - PRIMERO */}
              <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                <p className="text-zinc-400 font-medium mb-3 flex items-center gap-2 text-sm">
                  <Package className="w-4 h-4" />
                  TUS PRODUCTOS:
                </p>
                <AnimatePresence mode="popLayout">
                  {cart.length === 0 ? (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-6 text-zinc-500"
                    >
                      <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Agregá productos para ver cuánto ahorrás</p>
                    </motion.div>
                  ) : (
                    <div className="space-y-2">
                      {cart.map((item) => (
                        <motion.div
                          key={item.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 rounded-lg"
                        >
                          <div className="flex-1">
                            <p className="text-white text-sm font-medium">{item.name}</p>
                            <p className="text-zinc-500 text-xs">{item.category}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="text-zinc-500 line-through text-xs">
                                {formatPrice(item.originalPrice)}
                              </p>
                              <p className={`${config.text} font-bold text-sm`}>
                                {formatPrice(Math.round(item.discountedPrice))}
                              </p>
                            </div>
                            <span className="text-green-400 text-xs font-medium">
                              -{item.discountPercent}%
                            </span>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </AnimatePresence>
              </div>

              {/* Beneficios Incluidos - DESPUÉS */}
              <div className={`p-4 ${config.bg} rounded-xl border ${config.border}`}>
                <p className={`${config.text} font-medium mb-3 flex items-center gap-2 text-sm`}>
                  <Gift className="w-4 h-4" />
                  BENEFICIOS INCLUIDOS (Membresía {config.name}):
                </p>
                <div className="space-y-2">
                  {benefitItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between py-2 px-3 bg-zinc-900/50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{item.icon}</span>
                        <span className="text-zinc-300 text-sm">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-zinc-500 text-xs">x{item.quantity}</span>
                        <span className="text-zinc-500 line-through text-xs">
                          {formatPrice(item.estimatedValue)}
                        </span>
                        <span className="text-green-400 font-bold text-sm">GRATIS</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Resumen - Totals */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-6 ${config.bg} rounded-2xl border ${config.border} mb-6`}
          >
            <p className={`${config.text} font-bold mb-4 flex items-center gap-2`}>
              📊 RESUMEN
            </p>
            <div className="space-y-3">
              {/* Precio productos */}
              <div className="flex justify-between text-zinc-300">
                <span>Precio productos:</span>
                <span>{cart.length > 0 ? formatPrice(totalOriginal) : '₡0'}</span>
              </div>

              {/* Costo membresía */}
              <div className="flex justify-between text-zinc-300">
                <span className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  Costo membresía {config.name}:
                </span>
                <span>{formatPrice(membershipPrice)}</span>
              </div>

              <div className="border-t border-zinc-600 pt-2"></div>
              
              {/* Subtotal */}
              <div className="flex justify-between text-zinc-400">
                <span>Subtotal:</span>
                <span>{formatPrice(totalOriginal + membershipPrice)}</span>
              </div>

              {/* Descuento con membresía */}
              {cart.length > 0 && totalSavings > 0 && (
                <div className="flex justify-between text-green-400 font-medium">
                  <span>Descuento con membresía:</span>
                  <span>-{formatPrice(Math.round(totalSavings))}</span>
                </div>
              )}

              <div className={`border-t-2 ${config.border} pt-3`}></div>
              
              {/* Total Hoy */}
              <div className="flex justify-between items-center">
                <span className="text-white text-lg font-bold">TOTAL HOY:</span>
                <span className={`text-2xl font-bold ${config.text}`}>
                  {formatPrice(Math.round(grandTotal))}
                </span>
              </div>
            </div>

            {/* Valor de beneficios gratis */}
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-xl text-center">
              <Sparkles className="w-5 h-5 text-green-400 inline mr-2" />
              <span className="text-green-400 font-medium">
                ¡Además incluye beneficios por {formatPrice(totalBenefitsValue)} GRATIS!
              </span>
            </div>

            {/* Next Month Info */}
            <div className="mt-3 p-3 bg-zinc-800/50 border border-zinc-600 rounded-xl text-center">
              <p className="text-zinc-400 text-sm">
                💡 Próximo mes: solo <span className={`font-bold ${config.text}`}>{formatPrice(membershipPrice)}</span> con todos tus beneficios
              </p>
            </div>
          </motion.div>

          {/* CTA - Azul VillaTech */}
          <Button
            onClick={handleActivateMembership}
            className="w-full py-6 text-lg font-bold rounded-xl bg-gradient-to-r from-[#113B8A] to-[#1E5BB7] hover:from-[#0D2F6E] hover:to-[#113B8A] text-white shadow-[0_0_30px_rgba(17,59,138,0.4)] hover:shadow-[0_0_40px_rgba(17,59,138,0.6)] transition-all"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Activar Membresía {config.name}
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
